import * as React from 'react';
import { useRef, useState } from 'react';
import { Grid, Link as MuiLink, Typography } from '@mui/material';
import { Box, BoxOwnProps, Stack } from '@mui/system';
import { DynamicLogo } from 'components/core/logo';
import { ContactForm } from 'components/public/contact-form';
import { LandingPrimaryButton, LandingSecondaryButton, LandingTextButton } from 'components/public/landing';
import { paths } from 'paths';
import { Link } from 'react-router-dom';

const scrollToContactForm = () => {
  document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
};

const IssueSection: React.FC<{ title: string; description: string }> = ({ title, description }) => {
  return (
    <Box sx={{ borderTop: '1px solid #dfdfdf', paddingY: 3, paddingX: 2 }}>
      <Grid container spacing={{ xs: 1, md: 4 }}>
        <Grid item xs={12} md={4}>
          <Typography variant="body1" fontWeight={'500'}>
            {title}
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <Typography variant="body1">{description}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const FeatureSection: React.FC<{ number: string; title: string; description: string; img: string }> = ({
  number,
  img,
  title,
  description,
}) => {
  return (
    <Box sx={{ borderTop: '1px solid #dfdfdf', paddingY: 3, paddingX: 2 }}>
      <Grid container spacing={{ xs: 6, md: 6 }} alignItems={'center'} textAlign={{ xs: 'center', md: 'left' }}>
        <Grid item xs={12} md={2}>
          <Typography variant="h5" fontWeight={'100'} fontSize={'64px'} textAlign={{ xs: 'center', md: 'right' }}>
            {number}
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Typography variant="h5" fontWeight={'500'} fontSize={'22px'}>
            {title}
          </Typography>
        </Grid>
        <Grid item xs={12} md={3}>
          <Box component="img" src={img} sx={{ width: '100%' }} />
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="body1">{description}</Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

const SquareBox: React.FC<BoxOwnProps> = ({ children, sx, ...props }) => {
  return (
    <Box
      {...props}
      sx={{
        ...sx,
        borderRadius: '16px',
        overflow: 'hidden',
        justifyContent: 'center',
        display: 'flex',
        flexDirection: 'column',
        aspectRatio: 1,
        width: '100%',
      }}
    >
      {children}
    </Box>
  );
};

const Separator = () => <Box sx={{ borderTop: '1px solid #dfdfdf', width: '100%' }} />;

export default function LandingPage(): React.JSX.Element {
  const thisYear = new Date().getFullYear();
  const approachSectionRef = useRef<Element>(null);
  const featureSectionRef = useRef<Element>(null);
  const [contactFormMessage, setContactFormMessage] = useState<{ text: string; key: number } | undefined>();

  const scrollToContactFormWithMessage = (message: string) => {
    setContactFormMessage({ text: message, key: Date.now() });
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScroll = (ref?: React.RefObject<Element>) => {
    ref?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Stack spacing={5} direction={'column'}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          borderBottom: '1px solid #dfdfdf',
          justifyContent: 'center',
        }}
      >
        <Grid container maxWidth="lg" alignItems={'center'} sx={{ paddingX: 2 }}>
          <Grid item xs={6} sm={4}>
            <Box sx={{ paddingY: 2 }}>
              <DynamicLogo colorDark="light" colorLight="dark" height={60} width={222} />
            </Box>
          </Grid>
          <Grid item xs={6} sm={8} justifyItems={'end'}>
            <Stack direction={'row'} spacing={3} alignItems={'center'}>
              <LandingTextButton
                onClick={() => handleScroll(approachSectionRef)}
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Optimise benchmarking
              </LandingTextButton>
              <LandingTextButton
                onClick={() => handleScroll(featureSectionRef)}
                sx={{ display: { xs: 'none', sm: 'block' } }}
              >
                Features
              </LandingTextButton>
              <LandingSecondaryButton
                onClick={() =>
                  scrollToContactFormWithMessage(
                    'Please provide me with access to the demonstration environment of Benchmarking Portal.'
                  )
                }
              >
                Request access to demo
              </LandingSecondaryButton>
              <Link to={paths.app.auth.signIn}>
                <LandingPrimaryButton>Login</LandingPrimaryButton>
              </Link>
            </Stack>
            {/* TODO: primary czy zmieniamy? */}
          </Grid>
        </Grid>
      </Box>

      <Grid container maxWidth="lg" alignSelf={'center'} alignItems={'center'} sx={{ paddingX: { xs: 0, sm: 2 } }}>
        <Grid item xs={12}>
          <Box position={'relative'} textAlign={'right'}>
            <Box
              sx={{
                background: '#E9F073',
                paddingX: { xs: 2, sm: 4 },
                textAlign: 'left',
                paddingTop: { xs: 8, sm: 4 },
                paddingBottom: 4,
                position: { xs: 'static', sm: 'absolute' },
                width: { xs: '100%', sm: '50%' },
                bottom: { xs: 'unset', sm: '50px' },
                left: { xs: 'unset', sm: '0' },
              }}
            >
              <Typography variant="h5">Take Your Pharmaceutical Operations Performance to the Next Level</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ marginTop: 3 }}>
                <LandingSecondaryButton
                  onClick={() =>
                    scrollToContactFormWithMessage(
                      'Please provide me with access to the demonstration environment of Benchmarking Portal.'
                    )
                  }
                >
                  Request access to demo
                </LandingSecondaryButton>
              </Stack>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: { xs: 350, sm: 400, md: 600 },
                background: {
                  xs: 'url("/assets/main-xs.png") no-repeat center / cover',
                  sm: 'url("/assets/main.png") no-repeat right / auto 100%',
                },
              }}
            />
          </Box>
        </Grid>
      </Grid>

      <Grid container rowSpacing={3} maxWidth="lg" alignSelf={'center'} sx={{ paddingX: { xs: 0, sm: 2 } }}>
        <Grid item xs={12} md={3}>
          <Box sx={{ paddingX: 2 }}>
            <Typography variant="h5" sx={{ marginBottom: 2 }}>
              Finding Clarity in Complexity
            </Typography>
            <Typography variant="body1">
              Pharmaceutical manufacturing is intricate, making it difficult to know where to focus improvement efforts.
            </Typography>
          </Box>
        </Grid>
        <Grid item md={2} display={{ xs: 'none', md: 'block' }} />
        <Grid item xs={12} md={7}>
          <IssueSection
            title="No clear priorities"
            description="An overwhelming number of metrics make it hard to target high-value initiatives."
          />
          <IssueSection
            title="Lack of objective benchmarks"
            description="No systematic way to compare performance against industry peers."
          />
          <IssueSection
            title="Scattered data, unclear impact"
            description="Operational gaps are difficult to translate into measurable improvements."
          />
          <IssueSection
            title="High cost of access to static benchmarks"
            description="Existing solutions are expensive and offer only a one-time performance snapshot instead of an interactive experience."
          />
          <Separator />
        </Grid>
      </Grid>

      <Box ref={approachSectionRef} />

      <Grid container maxWidth="lg" alignSelf={'center'}>
        <Grid item xs={12} md={6} order={1}>
          <Box
            sx={{
              height: '100%',
              backgroundColor: '#FABD78',
              alignContent: 'center',
              paddingX: 8,
              borderRadius: '16px',
              aspectRatio: { xs: 1, md: 'unset' },
            }}
          >
            <Typography variant="h5">
              A Smarter Approach
              <br />
              to Improvement
            </Typography>

            <Typography variant="body1">
              Without clear benchmarks, improvement is guesswork. Benchmarking Portal brings precision to operational
              excellence.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={3} order={2}>
          <SquareBox sx={{ backgroundColor: '#EBECE5', padding: 6 }}>
            <Typography variant="h6">
              Identify
              <br />
              strengths & gaps
            </Typography>
            <Typography variant="body1">
              Gain a holistic, fact-based view of your plant's strengths and areas for improvement compared to industry
              peers.
            </Typography>
          </SquareBox>
        </Grid>
        <Grid item xs={12} md={3} order={3}>
          <SquareBox sx={{ backgroundColor: '#FADE77', padding: 6 }}>
            <Typography variant="h6">
              Quantify opportunities
              <br />& set targets
            </Typography>
            <Typography variant="body1">
              Measure deviations from industry benchmarks, express gaps in financial terms, and set ambitious yet
              achievable targets.
            </Typography>
          </SquareBox>
        </Grid>
        <Grid item xs={12} md={3} order={{ xs: 5, md: 4 }}>
          <SquareBox sx={{ backgroundColor: '#EBECE5', padding: 6 }}>
            <Typography variant="h6">
              Prioritise
              <br />
              OPEX efforts
            </Typography>
            <Typography variant="body1">
              Focus operational excellence initiatives on the areas with the highest potential impact to drive
              measurable improvements.
            </Typography>
          </SquareBox>
        </Grid>
        <Grid item xs={12} md={3} order={{ xs: 4, md: 5 }}>
          <SquareBox>
            <Box component="img" src="/assets/approach-1.png" sx={{ width: '100%' }} />
          </SquareBox>
        </Grid>
        <Grid item xs={12} md={3} order={6}>
          <SquareBox sx={{ backgroundColor: '#A9C571', padding: 6 }}>
            <Typography variant="h6">Secure cost-efficient access to data</Typography>
            <Typography variant="body1">
              Subscribe to a tool that provides continuous access to the latest benchmarks, enabling ongoing performance
              monitoring.
            </Typography>
          </SquareBox>
        </Grid>
        <Grid item xs={12} md={3} order={7}>
          <SquareBox>
            <Box component="img" src="/assets/approach-2.png" sx={{ width: '100%' }} />
          </SquareBox>
        </Grid>
      </Grid>

      <Grid container maxWidth="lg" alignSelf={'center'} spacing={5} marginY={{ xs: 0, md: 8 }}>
        <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
          <Box component="img" src="/assets/chart.png" sx={{ width: '100%' }} />
        </Grid>

        <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
          <Stack direction={'column'} spacing={3} sx={{ padding: 3 }}>
            <Typography variant="h5">
              Stop Guessing.
              <br />
              Start Benchmarking.
            </Typography>

            <Typography variant={'body1'}>
              Benchmarking Portal provides a clear, data-driven comparison of your company performance against industry
              leaders. Identify areas of excellence, pinpoint inefficiencies, and focus efforts where they will have the
              greatest impact.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <LandingPrimaryButton
                onClick={() =>
                  scrollToContactFormWithMessage(
                    'Please provide me with access to the demonstration environment of Benchmarking Portal.'
                  )
                }
              >
                Request access to demo
              </LandingPrimaryButton>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Grid container maxWidth="lg" alignSelf={'center'} spacing={5} marginY={{ xs: 0, md: 8 }}>
        <Grid item xs={12}>
          <Box ref={featureSectionRef}>
            <FeatureSection
              number="01"
              title="Compare objectively"
              img="/assets/feature-1.png"
              description="Measure your operations against peers with standardised, reliable data."
            />
            <FeatureSection
              number="02"
              title="Identify strengths & gaps"
              img="/assets/feature-2.png"
              description="Get a clear, fact-based view of where you excel and where you need improvement."
            />
            <FeatureSection
              number="03"
              title="Turn insights into action"
              img="/assets/feature-3.png"
              description="Prioritise high-impact improvements and maximise OPEX efficiency."
            />
            <FeatureSection
              number="04"
              title="Monitor your performance"
              img="/assets/feature-4.png"
              description="Track the outcomes of operational excellence efforts regularly to maintain momentum."
            />
            <Separator />
          </Box>
        </Grid>
      </Grid>

      <Grid
        id="contact-form"
        container
        maxWidth="lg"
        alignSelf={'center'}
        justifyContent={'center'}
        sx={{ paddingX: { xs: 2, sm: 2 }, paddingY: { xs: 4, md: 8 } }}
      >
        <Grid item xs={12} md={8}>
          <ContactForm initialMessage={contactFormMessage?.text} key={contactFormMessage?.key} />
        </Grid>
      </Grid>

      <Box sx={{ background: '#5D7243', paddingY: 5, paddingX: 2 }}>
        <Grid container maxWidth="lg" alignItems={'center'} sx={{ margin: '0 auto' }} rowSpacing={{ xs: 4, md: 0 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h5" color={'#E9F073'} fontWeight={400}>
              See where you stand.
              <br />
              Improve where it matters.
            </Typography>
          </Grid>
          <Grid item xs={12} md={8} textAlign={{ xs: 'center', md: 'right' }}>
            <Box component="img" src="/assets/logo-clip.png" sx={{ height: { xs: '110px', md: '150px' } }} />
          </Grid>

          <Grid item xs={12} sx={{ color: '#EBECE5' }}>
            <Box sx={{ borderTop: '1px solid #81906b', marginTop: 4, paddingTop: 2 }}>
              <Grid container>
                <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                  Powered by{' '}
                  <MuiLink href="https://clouddrift.co.uk" color={'#EBECE5'} target="_blank">
                    Cloud Drift
                  </MuiLink>
                  <Box component="img" src="/assets/logo-white.png" sx={{ marginLeft: 1, height: '18px' }} />
                </Grid>
                <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }} sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  &copy; Copyright {thisYear}{' '}
                  <MuiLink href="https://benchmarkingportal.com" color={'#EBECE5'}>
                    benchmarkingportal.com
                  </MuiLink>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}
